/**
 * useEffectiveUser.ts
 *
 * Returns the "effective" user for routing and auth guards.
 *
 * Priority:
 *  1. Valid (non-expired) impersonation token in sessionStorage
 *     → synthetic user with the impersonated role
 *  2. Real auth store user (normal login)
 *
 * This ensures that when a platform_admin is previewing as a teacher,
 * RequireAuth lets them into the teacher routes and the teacher layout renders.
 */

import { useAuthStore, type AuthUser } from '@/store/authStore'

export interface EffectiveUser extends Partial<AuthUser> {
  id:              string
  role:            string
  isImpersonating: boolean
}

function parseImpersonationClaims(): EffectiveUser | null {
  const token = sessionStorage.getItem('impersonation_token')
  if (!token) return null
  try {
    const [, b64] = token.split('.')
    if (!b64) return null
    const payload = JSON.parse(atob(b64)) as {
      sub?:           string
      role?:          string
      schoolId?:      string
      impersonation?: boolean
      exp?:           number
    }
    if (!payload.impersonation || !payload.sub || !payload.role) return null
    if ((payload.exp ?? 0) * 1000 <= Date.now()) {
      // Expired  -  clean up
      sessionStorage.removeItem('impersonation_token')
      sessionStorage.removeItem('impersonation_role')
      return null
    }
    return {
      id:              payload.sub,
      role:            payload.role,
      firstName:       undefined,
      lastName:        undefined,
      email:           undefined,
      avatarUrl:       undefined,
      isImpersonating: true,
    }
  } catch {
    return null
  }
}

export function useEffectiveUser(): EffectiveUser | null {
  const realUser = useAuthStore((s) => s.user)

  const impersonated = parseImpersonationClaims()
  if (impersonated) return impersonated

  if (!realUser) return null
  return { ...realUser, isImpersonating: false }
}

export function useIsLoading(): boolean {
  return useAuthStore((s) => s.isLoading)
}
